import { isValiError } from 'valibot';
import { Err, Ok, Queue, QueueIndex } from './types.ts';

const TAG = '[A Music]';

export const L = {
  info: (...args: unknown[]) => {
    console.info(TAG, ...args);
  },

  log: (...args: unknown[]) => {
    console.log(TAG, ...args);
  },

  warn: (...args: unknown[]) => {
    console.warn(TAG, ...args);
  },

  error: (...args: unknown[]) => {
    console.error(TAG, ...args);
  },
};

export class FetchError extends Error {
  errorCode: string;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'FetchError';
    this.message = message;
    this.errorCode = status.toString();
  }
}

export const isFetchError = (e: unknown): e is FetchError => {
  return e instanceof FetchError;
};

export const PLAYER_ASSERT = (
  player: MusicKit.MusicKitInstance | undefined,
) => {
  if (!player) {
    throw new Error('Player is not initialized');
  }
};

export const toOk = <T extends object>(value?: T): Ok<T> => {
  return value
    ? {
      ok: true,
      value,
    }
    : {
      ok: true,
    };
};

/**
 * Convert a error to a custom error format.
 * @param {unknown} e The error object
 * @returns {Err} An object containing the error message and code.
 */
export const toErr = (
  e: unknown,
): Err => {
  const [message, errorCode] = ((e) => {
    let message = 'Unknown error';
    let errorCode = 'UNKNOWN_APPLICATION_ERROR';

    if (isMKError(e) || isFetchError(e)) {
      message = e.message;
      errorCode = e.errorCode;
    } else if (isValiError(e)) {
      message = e.message;
      errorCode = 'VALIDATION_ERROR';
    } else if (e instanceof Error) {
      message = e.message;
      errorCode = 'SCRIPT_ERROR';
    }

    return [message, errorCode];
  })(e);

  return {
    ok: false,
    error: {
      message,
      errorCode,
    },
  };
};

/**
 * Check if the error is a MusicKit.MKError object.
 * @param {unknown} e The error object to check.
 * @returns {boolean} True if the error is a MusicKit.MKError object, false otherwise.
 */
export const isMKError = (e: unknown): e is MusicKit.MKError => {
  return e instanceof Error && e !== null && 'errorCode' in e && 'message' in e;
};

const isValidQueueIndex = (queue: QueueIndex): queue is QueueIndex => {
  if (!queue) {
    return false;
  }

  const { index } = queue;

  if (typeof index !== 'number' || index < 0) {
    return false;
  }

  return true;
};

export class AMusicError extends Error {
  errorCode: string;

  /**
   * Error code indicating that you don't have permission to access the
   * endpoint, media item, or content.
   */
  static ACCESS_DENIED: string;
  /**
   * Error code indicating the authorization was rejected.
   */
  static AUTHORIZATION_ERROR: string;
  /**
   * Error code indicating a MusicKit JS configuration error.
   */
  static CONFIGURATION_ERROR: string;
  /**
   * Error code indicating you don't have permission to access this content,
   * due to content restrictions.
   */
  static CONTENT_RESTRICTED: string;
  /**
   * Error code indicating the parameters provided for this method are invalid.
   */
  static INVALID_ARGUMENTS: string;
  /**
   * Error code indicating that the VM certificate could not be applied.
   */
  static MEDIA_CERTIFICATE: string;
  /**
   * Error code indicating that the media item descriptor is invalid.
   */
  static MEDIA_DESCRIPTOR: string;
  /**
   * Error code indicating that a DRM key could not be generated.
   */
  static MEDIA_KEY: string;
  /**
   * Error code indicating a DRM license error.
   */
  static MEDIA_LICENSE: string;
  /**
   * Error code indicating a media playback error.
   */
  static MEDIA_PLAYBACK: string;
  /**
   * Error code indicating that an EME session could not be created.
   */
  static MEDIA_SESSION: string;
  /**
   * Error code indicating a network error.
   */
  static NETWORK_ERROR: string;
  /**
   * Error code indicating that the resource was not found.
   */
  static NOT_FOUND: string;
  /**
   * Error code indicating that you have exceeded the Apple Music API quota.
   */
  static QUOTA_EXCEEDED: string;
  static SERVER_ERROR: string;
  /**
   * Error code indicating the MusicKit service could not be reached.
   */
  static SERVICE_UNAVAILABLE: string;
  /**
   * Error code indicating that the user's Apple Music subscription has expired.
   */
  static SUBSCRIPTION_ERROR: string;
  /**
   * Error code indicating an unknown error.
   */
  static UNKNOWN_ERROR: string;
  /**
   * Error code indicating that the operation is not supported.
   */
  static UNSUPPORTED_ERROR: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'AMuiscError';
    this.errorCode = errorCode ?? AMusicError.UNKNOWN_ERROR;
  }
}

export const isValidQueue = (queue: Queue | QueueIndex) => {
  if (!queue) {
    return false;
  }

  if ('index' in queue) {
    return isValidQueueIndex(queue);
  }

  const { type, id } = queue;

  if (!type || !id) {
    return false;
  }

  if (typeof type !== 'string' || typeof id !== 'string') {
    return false;
  }

  return true;
};

export const isQueue = (queue: Queue | QueueIndex): queue is Queue => {
  if (!queue) {
    return false;
  }
  return 'type' in queue && 'id' in queue;
};

export const isQueueIndex = (
  queue: QueueIndex | Queue,
): queue is QueueIndex => {
  if (!queue) {
    return false;
  }
  return 'index' in queue;
};

/**
 * Trim the queue to only include items after the specified index.
 * @param {Queue[]} queue The queue to trim.
 * @param {number} index The index to start trimming from.
 * @returns {Queue[]} The trimmed queue.
 */
export const trimQueue = (queue: Queue[], index: number): Queue[] => {
  if (!Array.isArray(queue)) {
    throw new TypeError('[trimQueue] queue must be an array');
  }
  if (typeof index !== 'number' || !Number.isInteger(index)) {
    throw new TypeError('[trimQueue] index must be an integer');
  }
  if (index < 0 || index > queue.length) {
    throw new RangeError(
      `[trimQueue] index ${index} is out of bounds (0 to ${queue.length})`,
    );
  }
  return queue.slice(index);
};

/**
 * Convert a media type string to key of the queue.
 * @param {string} type The media type string to convert.
 * @param {'NativeType' | 'OriginalType'} to The type to convert to.
 * @returns {string} The converted queue key.
 * @example
 *   ```ts
 *   await music.setQueue({
 *     [toMediaType(type)]: id,
 *   });
 *   ```
 */
export const toMediaType = (
  type: string,
  to: 'NativeType' | 'OriginalType' = 'OriginalType',
): string => {
  switch (type) {
    case 'album':
    case 'albums':
    case 'library-albums':
    case 'library-album':
      return 'album';
    case 'playlist':
    case 'playlists':
    case 'library-playlists':
    case 'library-playlist':
      return 'playlist';
    case 'track':
    case 'song':
      return to === 'NativeType' ? 'track' : 'song';
    case 'tracks':
    case 'songs':
    case 'library-songs':
    case 'library-song':
      return to === 'NativeType' ? 'track' : 'songs';
    case 'station':
    case 'stations':
      return 'station';
    case 'music-video':
    case 'music-videos':
    case 'library-music-videos':
    case 'musicVideo':
    case 'musicVideos':
      return to === 'NativeType' ? 'music-video' : 'musicVideo';
    default:
      L.warn('Unknown media type:', type);
      return type;
  }
};

/**
 * Get the offset number from the URL.
 * @param {string} url The URL to parse.
 * @returns {number} The offset number, or 0 if not found.
 */
export const getOffsetNumber = (url: string) => {
  try {
    const query = url.split('?')[1];
    const params = new URLSearchParams(query);
    const offset = params.get('offset');
    return offset ? parseInt(offset) : 0;
  } catch {
    return 0;
  }
};

/**
 * Take the first n elements from an array.
 * @param {T[]} arr The array to take from.
 * @param {number} max The maximum number of elements to take.
 * @returns {T[]} The taken elements.
 */
export const taken = <T>(arr: T[], max: number = 3): T[] => {
  return arr.splice(0, max);
};

export const camelToKebab = (str: string): string => {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
};
